import type { ApiErrorShape } from "../types.js";

/**
 * Minimal API client.
 *  - The access token lives ONLY in memory (never localStorage) to limit XSS
 *    blast radius. A page reload silently re-bootstraps it from the refresh
 *    cookie via /auth/refresh.
 *  - On a 401 the client transparently attempts ONE refresh, then retries.
 */

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

/* --- Read-only demo interception ------------------------------------------- */
// A demo login may fill in and explore every form, but its writes must never
// reach the server. We intercept mutating requests HERE (before the fetch) so
// no data is sent, surface a localized "changes are disabled" warning, and
// throw so the caller's success path never runs. The API also rejects demo
// writes (DEMO_READ_ONLY 403) as the real, defense-in-depth guarantee.

let demoMode = false;
/** Kept in sync with the current user's `demo` flag by the auth context. */
export function setDemoMode(on: boolean): void {
  demoMode = on;
}

// Set by a component with access to the toast + i18n so the low-level client
// can show a localized warning. Returns the message to reuse in the thrown error.
let demoWriteNotifier: (() => string) | null = null;
export function setDemoWriteNotifier(fn: (() => string) | null): void {
  demoWriteNotifier = fn;
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
// Session-lifecycle endpoints a demo login must still reach — authenticating or
// ending a session is never a "demo write". Self-service account mutations
// (/auth/password, /auth/mfa/*, /auth/sessions/*) are deliberately NOT here, so
// they surface the read-only warning like any other write.
const DEMO_ALLOWED_PATHS = new Set([
  "/auth/login",
  "/auth/register",
  "/auth/mfa/verify",
  "/auth/password/expired",
  "/auth/logout",
  "/auth/refresh",
]);

/** Returns a blocking error (and fires the warning toast) when a demo login writes. */
function demoWriteBlock(method: string, path: string): ApiError | null {
  if (!demoMode) return null;
  if (!MUTATING_METHODS.has(method.toUpperCase())) return null;
  if (DEMO_ALLOWED_PATHS.has(path.split("?")[0] ?? path)) return null;
  const message = demoWriteNotifier?.() ?? "This is a read-only demo account; changes are disabled";
  return new ApiError(403, "DEMO_READ_ONLY", message, undefined, "AUTH_DEMO_READ_ONLY");
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    /** Granular server reason (or a client-only reason for transport failures). */
    readonly reason?: string,
    /** Correlation id for support, when the server supplied one. */
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorShape | null = null;
  try {
    body = (await res.json()) as ApiErrorShape;
  } catch {
    /* non-JSON */
  }
  // A JSON error body is the contract. A non-JSON 5xx (a proxy's HTML 502/503,
  // a crashed upstream) is NOT an application error — surface it as an
  // unavailable-server reason so the panel says "try again", not a raw status.
  if (!body?.error) {
    if (res.status >= 500) {
      return new ApiError(res.status, "SERVER_UNAVAILABLE", res.statusText, undefined, "SERVER_UNAVAILABLE");
    }
    return new ApiError(res.status, "HTTP_ERROR", res.statusText);
  }
  const correlationId = body.error.correlationId ?? res.headers.get("x-correlation-id") ?? undefined;
  return new ApiError(
    res.status,
    body.error.code ?? "HTTP_ERROR",
    body.error.message ?? res.statusText,
    body.error.details,
    body.error.reason,
    correlationId,
  );
}

/** Attempt a single token refresh; coalesces concurrent callers. */
export async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch("/auth/refresh", { method: "POST", credentials: "include" });
        if (!res.ok) return false;
        const data = (await res.json()) as { tokens: { accessToken: string } };
        accessToken = data.tokens.accessToken;
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip the automatic refresh-and-retry (used by auth endpoints themselves). */
  noRetry?: boolean;
}

// Requests that hang longer than this are aborted and surfaced as a TIMEOUT so a
// dead upstream doesn't leave the user staring at a spinner forever.
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Run a fetch, converting transport failures into an ApiError with a client-only
 * reason so they flow through the SAME error catalog as server errors. A
 * `fetch()` rejection (offline, DNS, connection refused) is a `TypeError`; an
 * abort is our own timeout.
 */
async function safeFetch(run: (signal: AbortSignal) => Promise<Response>): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await run(ctrl.signal);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new ApiError(0, "TIMEOUT", "The request timed out", undefined, "NETWORK_TIMEOUT");
    }
    throw new ApiError(0, "NETWORK_ERROR", "Could not reach the server", undefined, "NETWORK_UNREACHABLE");
  } finally {
    clearTimeout(timer);
  }
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const blocked = demoWriteBlock(opts.method ?? "GET", path);
  if (blocked) throw blocked;

  const doFetch = (): Promise<Response> =>
    safeFetch((signal) =>
      fetch(path, {
        method: opts.method ?? "GET",
        credentials: "include",
        signal,
        headers: {
          ...(opts.body !== undefined ? { "content-type": "application/json" } : {}),
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        },
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      }),
    );

  let res = await doFetch();

  if (res.status === 401 && !opts.noRetry && (await tryRefresh())) {
    res = await doFetch();
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** POST raw binary bytes (e.g. a product image) with the same auth + refresh-retry semantics. */
export async function apiUpload<T>(path: string, body: Blob, contentType: string): Promise<T> {
  const blocked = demoWriteBlock("POST", path);
  if (blocked) throw blocked;

  const doFetch = (): Promise<Response> =>
    safeFetch((signal) =>
      fetch(path, {
        method: "POST",
        credentials: "include",
        signal,
        headers: {
          "content-type": contentType,
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        },
        body,
      }),
    );

  let res = await doFetch();
  if (res.status === 401 && (await tryRefresh())) {
    res = await doFetch();
  }
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

/** GET a binary resource (e.g. QR image) with the same auth + refresh-retry semantics. */
export async function apiBlob(path: string): Promise<Blob> {
  const doFetch = (): Promise<Response> =>
    safeFetch((signal) =>
      fetch(path, {
        credentials: "include",
        signal,
        headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
      }),
    );

  let res = await doFetch();
  if (res.status === 401 && (await tryRefresh())) {
    res = await doFetch();
  }
  if (!res.ok) throw await parseError(res);
  return res.blob();
}
